import { useCallback, useContext, useMemo, useState } from 'react';
import { AnchoredSheet } from '@shared/components/AnchoredSheet';
import { MBButton } from '@shared/components/MBButton';
import { View } from '@shared/components/View';
import { MBQuestionInputActionButton } from '@shared/components/blocks/lib/inputQuestion/actionButton';
import { MBAutosizeTextInput } from '@shared/components/textInput';
import { ColorThemeContext, grid } from '@shared/styles';
import { Trigger, useBoolean, useExecuteOnTrigger } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';

interface Props {
  label: string;
  placeholder: string;
  value: string;
  onChange(value: string): void;
  trigger: Trigger;
  isReadOnly?: boolean;
  anchorName: string;
}

export function MBQuestionNotes({
  label,
  placeholder,
  value,
  onChange,
  trigger,
  isReadOnly,
  anchorName,
}: Props) {
  const colors = useContext(ColorThemeContext);
  const [sheetVisible, showSheet, hideSheet] = useBoolean(false);
  const [text, setText] = useState(value);
  const hideSheetAndSave = useCallback(() => {
    setText(text);
    onChange(text);
    hideSheet();
  }, [hideSheet, onChange, text]);

  useExecuteOnTrigger(trigger, showSheet);

  const notesForm = useMemo(() => {
    return (
      <View padding={2}>
        <MBAutosizeTextInput
          style={{ backgroundColor: colors.backgroundInactive, borderRadius: grid(1) }}
          onChangeText={setText}
          value={text}
          placeholder={placeholder}
          autoFocus
          padding="normal"
        />
        <View paddingBottom={2} />
        <MBButton title={i18n('common.ok')} onPress={hideSheetAndSave} />
      </View>
    );
  }, [colors.backgroundInactive, hideSheetAndSave, placeholder, text]);

  return (
    <>
      <MBQuestionInputActionButton
        label={label}
        onPress={showSheet}
        icon="multi-line"
        disabled={isReadOnly}
        anchorName={anchorName}
      />
      {sheetVisible && (
        <AnchoredSheet
          initialSize="half"
          onClose={hideSheet}
          title={label}
          anchorName={anchorName}
          style={{ minWidth: grid(40) }}
        >
          {notesForm}
        </AnchoredSheet>
      )}
    </>
  );
}
